import { cn } from "@/lib/utils";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Icon } from "@/components/ui/Icon";

const Select = SelectPrimitive.Root;
/** The value truncates inside the trigger, so a long label never pushes the chevron out. */
function SelectValue({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return (
    <span className={cn("min-w-0 truncate", className)}>
      <SelectPrimitive.Value {...props} />
    </span>
  );
}

function SelectTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger
      className={cn(
        "flex items-center gap-1 rounded-full border border-transparent bg-canvas",
        "px-2 py-0.5 text-[11.5px] font-chrome font-medium text-muted",
        "outline-none focus-visible:outline-none cursor-pointer transition-colors duration-100",
        "hover:text-ink",
        "focus:text-ink",
        "data-[placeholder]:text-placeholder",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon className="shrink-0">
        <Icon name="chevron-up-down" size={12} className="opacity-60" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = "popper",
  onCloseAutoFocus,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        position={position}
        sideOffset={4}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          onCloseAutoFocus?.(e);
        }}
        className={cn(
          // Same surface as DropdownMenuContent, so every menu in the app matches.
          // Above dialogs (z-200), since selects also live inside Settings.
          "z-[250] max-h-72 min-w-[10rem] overflow-y-auto rounded-2xl border",
          "bg-canvas border-border font-chrome text-ink",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1",
          className,
        )}
        {...props}
      >
        <SelectPrimitive.Viewport className="p-1">
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      className={cn(
        // Same row as DropdownMenuItem.
        "relative flex items-center gap-2 rounded-full px-2.5 py-1.5 text-[13px] max-md:py-2.5 max-md:text-[16px] cursor-pointer select-none",
        "outline-none focus-visible:outline-none focus:outline-none",
        "text-ink-secondary transition-colors duration-100",
        "data-[highlighted]:bg-hover data-[highlighted]:text-ink",
        "data-[state=checked]:text-accent-text data-[state=checked]:font-medium",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="ml-auto">
        <Icon name="check" size={13} />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

export { Select, SelectContent, SelectItem, SelectTrigger, SelectValue };
